import { useEffect, useState } from 'react';
import { useDialogAction } from '@mews/framework';
import { PortfolioCentralProductAddParameters } from '@mews-commander/data-access';
import type { FormErrors } from '../../enterpriseRateManagement/types';
import { useEnterprisesProductContext } from '../context/EnterprisesProductContext';
import { useAddCentralProducts, useUpdateCentralProducts } from '../queries/ProductForm';
import { handleImagePayload } from '../utils/handleImagePayload';
import type { EditFormValues, FormValues } from '../components/CentralProductForm/formEnums';

interface Props {
    centralProductId?: string;
    handleAddToProperty?: () => void;
}

export const useCentralProductForm = ({ centralProductId, handleAddToProperty }: Props) => {
    const { setState } = useEnterprisesProductContext();

    const addCentralProducts = useAddCentralProducts();
    const updateCentralProducts = useUpdateCentralProducts();

    const { states, handlers } = useDialogAction(async () => Promise.resolve());

    const [formErrors, setFormErrors] = useState<FormErrors<FormValues>>();

    const handleProductKeyError = (e: Error) => {
        if (e.message.includes('product key')) {
            setFormErrors({ field: 'key' as keyof FormValues, message: e.message });
        }
    };
    const handleSubmit = async (props: FormValues | EditFormValues) => {
        const { image, externalIdentifier, externalName, name, shortName, ordering, description, imageId } = props;
        const imageValue = await handleImagePayload(image);

        // Omitting values from edit such as editor/creator info from sending to BE
        const cleanedValues = {
            ...(props && 'key' in props ? { key: props.key } : {}),
            externalIdentifier,
            externalName,
            name,
            shortName,
            ordering,
            description,
            image: imageValue,
        } as PortfolioCentralProductAddParameters;

        if (centralProductId) {
            return updateCentralProducts.mutate(
                { ...cleanedValues, centralProductId, imageId },
                { onSuccess: async () => handlers.handleConfirm(), onError: handleProductKeyError }
            );
        }
        return addCentralProducts.mutate(
            { ...cleanedValues },
            {
                onSuccess: (data) => {
                    if (!data || !data.centralProduct) {
                        return;
                    }

                    setState({
                        centralProduct: {
                            id: data.centralProduct.id,
                        },
                        enterprises: [],
                    });

                    if (handleAddToProperty) handleAddToProperty();
                },
                onError: handleProductKeyError,
            }
        );
    };

    useEffect(() => {
        if (!states.isDialogOpened) {
            setFormErrors(undefined);
        }
    }, [states.isDialogOpened]);

    return {
        states,
        handlers,
        handleSubmit,
        formErrors,
        isPending: addCentralProducts.isPending || updateCentralProducts.isPending,
    };
};
